import { Inject, Injectable, Optional } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { NavigationStart, Router } from '@angular/router';
import { Response } from 'express';
import { filter } from 'rxjs';
import { RESPONSE } from '../../express.tokens';

/** The directive set every indexable page sends; `index, follow` first because that is what QA checks for. */
export const ROBOTS_INDEX = 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';
export const ROBOTS_NOINDEX = 'noindex';

/**
 * Single writer of the `robots` meta tag.
 *
 * Two sources decide a page's indexability: the old-CMS `siteMap` flag, applied by
 * `PageLoadService.updatePageMetaTags` for every page, and pages that decide for themselves (the tutor
 * profile, from the tutor's search visibility). On client-side navigation these arrive in either order, so
 * a page-owned decision is remembered as an override that the CMS default cannot clobber. The override is
 * dropped on the next `NavigationStart`, so it never leaks to another page.
 *
 * On the server a page-owned decision also rewrites the `X-Robots-Tag` header, which `website.endpoint.ts`
 * sets to `noindex` before the render whenever the CMS page has no `siteMap`. Headers stay mutable until
 * the rendered HTML is sent, and Google treats header and meta tag as one rule (most restrictive wins),
 * so both must agree.
 */
@Injectable({ providedIn: 'root' })
export class RobotsService {
  private override: string | null = null;

  constructor(
    private meta: Meta,
    router: Router,
    @Optional() @Inject(RESPONSE) private response: Response | null,
  ) {
    router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(() => this.override = null);
  }

  /** A page that owns its indexability. Wins over the CMS default for the rest of this navigation. */
  set(content: string): void {
    this.override = content;
    this.meta.updateTag({ name: 'robots', content });
    if (this.response && !this.response.headersSent) {
      this.response.setHeader('X-Robots-Tag', content);
    }
  }

  /** The CMS `siteMap` rule. Only written when no page-owned decision has been made for this navigation. */
  applyDefault(content: string): void {
    this.meta.updateTag({ name: 'robots', content: this.override ?? content });
  }
}
